import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import MarkdownReader from "@/components/affiliate/MarkdownReader";
import ProtocolCard from "@/components/injection/ProtocolCard";
import VialStatus from "@/components/injection/VialStatus";
import { getFoundations, getProtocol, startVial, incrementVialDose, estimateTotalDoses } from "@/lib/injectionApi";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

// Member injection reference library. Persistent companion to the
// member dashboard — accessible from /app/reference/injections, also
// linked from the "My Injections" dashboard surface.
//
// Three tabs:
//   · CARD          — member's protocol card (printable) + vial status
//   · FOUNDATIONS   — the long-form guide
//   · QUICK REFS    — six tap-open quick reference cards
//
export default function InjectionReference() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("card");
  const [foundations, setFoundations] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [vial, setVial] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      const [f, p] = await Promise.all([
        getFoundations().catch(() => null),
        getProtocol().catch(() => null),
      ]);
      setFoundations(f);
      setProtocol(p);
      // Vial is server-authoritative — comes nested on the protocol.
      // No localStorage fallback; an unfinished BE returns no vial and
      // the empty-state card renders.
      setVial(p?.vial && p.vial.reconstitutedAt ? p.vial : null);
    })();
  }, []);

  const refreshProtocol = async () => {
    const p = await getProtocol().catch(() => null);
    if (p) {
      setProtocol(p);
      setVial(p?.vial && p.vial.reconstitutedAt ? p.vial : null);
    }
  };

  const startNewVial = async () => {
    try {
      const updated = await startVial({ totalDoses: estimateTotalDoses(protocol) });
      // BE returns the canonical vial; fall back to refetching the
      // protocol if the endpoint returned a thin ack so the UI still
      // hydrates correctly.
      if (updated && updated.reconstitutedAt) setVial(updated);
      else await refreshProtocol();
      toast("Vial logged — we'll track expiration for you.", "success");
    } catch (err) {
      toast(err?.detail || err?.message || "Couldn't start a new vial — please try again.", "error");
    }
  };

  const markDoseUsed = async () => {
    try {
      const updated = await incrementVialDose();
      if (updated && updated.reconstitutedAt) setVial(updated);
      else await refreshProtocol();
      toast("Dose logged.", "success");
    } catch (err) {
      toast(err?.detail || err?.message || "Couldn't log that dose — please try again.", "error");
    }
  };

  return (
    <div data-testid="injection-reference" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${C.warmGray3}`, background: C.cream, position: "sticky", top: 0, zIndex: 30 }}>
        <Logo size={20} />
        <button
          type="button"
          onClick={() => navigate("/app")}
          data-testid="reference-back"
          style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", cursor: "pointer" }}
        >
          ← DASHBOARD
        </button>
      </header>

      <div style={{ maxWidth: 880, margin: "0 auto", padding: "32px 24px 12px" }}>
        <Eyebrow color={C.peachDeep}>★ REFERENCE LIBRARY</Eyebrow>
        <Headline before="EVERYTHING ABOUT YOUR" italic="injections." size={44} className="mt-4" />
      </div>

      {/* Tabs */}
      <nav
        data-testid="reference-tabs"
        className="no-scrollbar"
        style={{
          display: "flex",
          gap: 2,
          padding: "0 24px",
          borderBottom: `1px solid ${C.warmGray3}`,
          maxWidth: 880,
          margin: "20px auto 0",
          overflowX: "auto",
        }}
      >
        {[
          { id: "card", label: "PROTOCOL CARD" },
          { id: "foundations", label: "FOUNDATIONS" },
          { id: "quickrefs", label: "QUICK REFS" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`reference-tab-${t.id}`}
            style={{
              padding: "14px 18px",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.2em",
              background: "transparent",
              border: 0,
              borderBottom: tab === t.id ? `2px solid ${C.peachDeep}` : "2px solid transparent",
              color: tab === t.id ? C.ink : C.warmGray,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main style={{ maxWidth: 880, margin: "0 auto", padding: "28px 24px 80px" }}>
        {tab === "card" && (
          <div>
            {protocol === null ? <Spinner size={20} /> : <ProtocolCard protocol={protocol} showDownload testId="reference-protocol-card" />}
            <p style={{ marginTop: 18, fontSize: 12, color: C.warmGray, fontStyle: "italic", fontFamily: '"Instrument Serif", Georgia, serif' }}>
              Download saves a print-ready A4 copy. The card is your safest reference — keep one on the kit.
            </p>

            <div style={{ marginTop: 32 }}>
              <VialStatus vial={vial} onMarkUsed={vial?.reconstitutedAt ? markDoseUsed : null} testId="reference-vial" />
              <div style={{ marginTop: 14, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Button
                  variant={vial?.reconstitutedAt ? "ghost" : "primary"}
                  size="md"
                  testId="reference-start-vial"
                  onClick={startNewVial}
                >
                  {vial?.reconstitutedAt ? "START A NEW VIAL" : "LOG MY FIRST VIAL →"}
                </Button>
                <Button variant="peach" size="md" testId="reference-log-injection" onClick={() => navigate("/app/injection/log")}>
                  + LOG AN INJECTION
                </Button>
                <Button variant="ghost" size="md" testId="reference-view-history" onClick={() => navigate("/app/injection/history")}>
                  VIEW HISTORY
                </Button>
              </div>
            </div>
          </div>
        )}

        {tab === "foundations" && (
          <div>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the foundations guide…"
              data-testid="foundations-search"
              style={{ width: "100%", padding: "12px 14px", fontSize: 14, background: C.pure, border: `1px solid ${C.warmGray3}`, outline: "none", marginBottom: 22 }}
            />
            {foundations === null ? (
              <Spinner size={20} />
            ) : foundations?.body_md ? (
              <MarkdownReader source={filterFoundations(foundations.body_md, query)} testId="reference-foundations" />
            ) : (
              <div data-testid="foundations-empty" style={{ padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
                The Foundations guide will appear here as soon as your protocol is finalized.
              </div>
            )}
          </div>
        )}

        {tab === "quickrefs" && <QuickRefs />}
      </main>
    </div>
  );
}

// Tiny client-side search — filter ##-section blocks that contain the
// query, similar to the affiliate playbook reader. Keeps things simple
// while BE-side search is being built.
function filterFoundations(md, q) {
  if (!q.trim()) return md;
  const sections = md.split(/\n(?=##\s)/);
  const matched = sections.filter((s) => s.toLowerCase().includes(q.toLowerCase()));
  return matched.length > 0
    ? matched.join("\n\n")
    : `_No matches for "${q}". Try different words._`;
}

function QuickRefs() {
  // Pulled directly from the Foundations spec — these are the topics
  // members reach for most often during injection prep.
  const items = [
    {
      title: "HOW TO RECONSTITUTE",
      bullets: [
        "Use bacteriostatic water — NOT sterile water, saline, or tap water",
        "Slowly push the plunger so water flows down the wall, not into the powder",
        "Roll the vial gently between your palms — never shake",
        "Wait until the solution is clear before drawing your dose",
      ],
    },
    {
      title: "HOW TO INJECT",
      bullets: [
        "Wash hands, alcohol-swab the site, let it air-dry",
        "Pinch a fold of skin, insert at 90° (or 45° if very lean)",
        "Push the plunger slowly — count to five",
        "Withdraw, apply gentle pressure with the swab",
        "Drop the needle straight into the sharps container — never recap",
      ],
    },
    {
      title: "WHEN TO CALL CARE TEAM",
      bullets: [
        "Redness, warmth, or hardness lasting > 24 hours",
        "Fever, expanding redness, pus, or signs of infection",
        "Severe injection-site pain (beyond a mild sting)",
        "Suspected dosing error",
        "New pregnancy or hospitalization",
        "Anything that doesn't match what your physician told you",
      ],
      accent: true,
    },
    {
      title: "WHEN TO CALL 911",
      bullets: [
        "Trouble breathing or swallowing",
        "Swelling of face, lips, or throat",
        "Severe rash spreading rapidly",
        "Any other emergency",
      ],
      urgent: true,
    },
    {
      title: "TROUBLESHOOTING",
      bullets: [
        "Powder won't dissolve → wait 5 more minutes, roll gently. Still cloudy? Don't inject — call us",
        "Air bubble in syringe → tap, flick to top, push gently to expel (subcutaneous air is not dangerous, just stings)",
        "Bleeding > 2 minutes → apply sustained pressure",
        "Forgot a dose → take the next scheduled one. Don't double up",
      ],
    },
    {
      title: "STORAGE & HANDLING",
      bullets: [
        "Unmixed vials: refrigerate at 2–8°C unless told otherwise",
        "Reconstituted vials: refrigerate, use within 30 days",
        "Don't freeze unless your protocol card says so",
        "Travel: keep cool packs around the vial in an insulated bag",
      ],
    },
  ];
  return (
    <div data-testid="quickrefs-list" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
      {items.map((it, i) => (
        <article
          key={i}
          data-testid={`quickref-${i}`}
          style={{
            background: it.urgent ? C.coral : it.accent ? C.peach : C.pure,
            color: it.urgent ? C.cream : C.ink,
            border: it.urgent ? "none" : `1px solid ${it.accent ? C.peachDeep : C.warmGray3}`,
            padding: 20,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, letterSpacing: "0.16em" }}>{it.title}</h3>
          <ul style={{ margin: "12px 0 0", paddingLeft: 20, fontSize: 13, lineHeight: 1.65 }}>
            {it.bullets.map((b, j) => <li key={j} style={{ marginBottom: 6 }}>{b}</li>)}
          </ul>
        </article>
      ))}
    </div>
  );
}
